<template>
  <div>
    <div class="f m-t4 m-bx4">
      <div class="t m-rx1">Filters:</div>
      <donut-selector-prefabs-user v-model="userSelection" :is-multi="true" class="w-x30 m-rx1" />
      <donut-selector-prefabs-date-range :update-range="updateDateRange" :include-weekends="true" />
    </div>
    <donut-table :headers="tableHeaders" :data="tableData">
      <template #event="{ entry }"><div class="t">{{ title(entry) }}</div></template>
      <template #points="{ entry }"><div v-emoji="pointsWithEmoji(entry)" class="t" /></template>
      <template #user="{ entry }">
        <div class="f f-gap-8">
          <onboarding-membership-avatar :avatar="entry.user.avatar" />
          <div class="t">{{ entry.user.name }}</div>
        </div>
      </template>
      <template #sender="{ entry }">
        <div v-if="entry.sender" class="f f-gap-8">
          <onboarding-membership-avatar :avatar="entry.sender.avatar" />
          <div class="t">{{ entry.sender.name }}</div>
        </div>
        <div v-else class="t">Annual Event</div>
      </template>
    </donut-table>
    <div v-if="isFetching" class="f-jc m-bx2"><donut-loading-dots /></div>
    <div v-else-if="!tableData.length" class="f-jc m-tx4"><div class="t">{{ emptyCopy }}</div></div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, watch } from 'vue';
  import useInfiniteScroll from 'composables/infinite-scroll';
  import Network from 'lib/api/network';
  import { capitalize } from 'lodash';

  const props = defineProps({
    pointsConfig: { type: Object, required: true },
  });

  // Filtering
  const userSelection = ref([]);
  const userIds = computed(() => userSelection.value.map(u => u.id).join('-') || null);
  const startDate = ref(null);
  const endDate = ref(null);
  // Data Collections
  const collectionKey = computed(() => `${startDate.value}_${endDate.value}_${userIds.value}`);
  const collections = ref({});
  const collection = computed(() => collections.value[collectionKey.value]);

  // Table Headers and Data
  const tableHeaders = [
    { key: 'event', label: 'Bonus Event', weight: 2 },
    { key: 'points', label: props.pointsConfig.points_name_plural, weight: 2 },
    { key: 'date', label: 'Date', weight: 2 },
    { key: 'user', label: 'Recipient', weight: 3 },
    { key: 'sender', label: 'Sender', weight: 3 },
  ];
  const tableData = computed(() => collection.value?.list || []);

  // Points
  const pointsWithEmoji = (event) => `${event.points.toLocaleString()} :${props.pointsConfig.emoji}:`;

  // Data Fetching
  const nextPage = computed(() => (collection.value ? collection.value.nextPage : 1));
  const isFetching = ref(false);
  const fetchData = () => {
    isFetching.value = true;
    return new Promise(() => {
      let path = `/bonus/events?page=${nextPage.value}`;
      if (startDate.value) path += `&start_date=${startDate.value}`;
      if (endDate.value) path += `&end_date=${endDate.value}`;
      if (userIds.value) path += `&user_ids=${userIds.value}`;
      Network.get(path, {
        success: (data) => {
          if (collection.value) {
            // Have already loaded events for this particular filter query combo. Add to the existing list, update the nextPage value.
            collections.value[collectionKey.value] = { list: collection.value.list.concat(data.list), nextPage: data.nextPage };
          } else {
            // Have not loaded events for this particular filter query combo. Initiate the whole thing.
            collections.value[collectionKey.value] = data;
          }
        },
        complete: () => { isFetching.value = false; },
      });
    });
  };
  const fetchDataIfNecessary = () => { if (!collection.value) fetchData(); };
  const shouldFetchAtEndOfScroll = computed(() => nextPage.value && !isFetching.value);
  const { fetchFirstPage } = useInfiniteScroll({ fetchData, shouldFetchAtEndOfScroll });

  // Filter Methods
  const updateDateRange = (range) => {
    startDate.value = range.startDate;
    endDate.value = range.endDate;
    fetchDataIfNecessary();
  };
  watch(userIds, () => {
    fetchDataIfNecessary();
  });

  // Event Copy
  const title = (event) => event.template?.title || capitalize(event.milestone_config.milestone_type);

  // Empty State
  const emptyCopy = computed(() => {
    let copy = 'You haven\'t sent any bonuses yet!';
    if (userIds.value || startDate.value || endDate.value) copy += ' Try adjusting the filters.';
    return copy;
  });

  // Lifecycle Hooks
  onMounted(() => {
    fetchFirstPage();
  });
</script>
